import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { usePricingActions } from "../hooks/use-pricing-actions";
import { isModifiedClick } from "@/lib/link-utils";

interface BillingCycleToggleProps {
  selectedCycle: "monthly" | "yearly" | "one-time";
  onCycleChange: (cycle: "monthly" | "yearly" | "one-time") => void;
  showOneTime?: boolean;
}

export const BillingCycleToggle: React.FC<BillingCycleToggleProps> = ({
  selectedCycle,
  onCycleChange,
  showOneTime = true,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Zero prop drilling - kendi hook'unu kullan
  const { selectedPlan } = usePricingActions();

  // Yalnızca `cycle` değişsin; diğer query param'lar korunsun.
  const buildCycleHref = (cycle: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("cycle", cycle);
    return `?${params.toString()}`;
  };
  // Dinamik indirim hesaplaması
  const getDiscountForCycle = (cycle: string) => {
    if (cycle === "yearly") {
      if (selectedPlan && selectedPlan.discountPercentage > 0) {
        // Seçili planın indirim yüzdesini kullan
        return `-${selectedPlan.discountPercentage}%`;
      } else {
        // Varsayılan indirim
        return "-30%";
      }
    }
    return null;
  };

  const cycles = [
    { value: "monthly" as const, label: t("pricing.monthly"), discount: null },
    {
      value: "yearly" as const,
      label: t("pricing.annually"),
      discount: getDiscountForCycle("yearly"),
    },
    ...(showOneTime
      ? [
          {
            value: "one-time" as const,
            label: t("pricing.oneTime"),
            discount: null,
          },
        ]
      : []),
  ];

  return (
    <div className="inline-flex">
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-1 inline-flex">
        {cycles.map((cycle) => (
          <Link
            key={cycle.value}
            to={buildCycleHref(cycle.value)}
            replace
            onClick={(e) => {
              if (isModifiedClick(e)) return; // Ctrl/Cmd/orta tık → tarayıcı yeni sekmede açsın
              onCycleChange(cycle.value);
            }}
            className={cn(
              "relative px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-2 justify-center whitespace-nowrap",
              selectedCycle === cycle.value
                ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-100 dark:border-gray-700 shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            )}
          >
            <span>{cycle.label}</span>
            {cycle.discount && cycle.value === "yearly" && (
              <span className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 text-xs font-medium px-2 py-0.5 rounded-md">
                {cycle.discount}
              </span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};
